import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Export,
  Plus,
  PrimaryButton,
  Spinner,
  Tab,
  TabContent,
  TabList,
  TabsProvider,
  TimerIcon,
  ViewIcon,
} from '@weareopenr/component-library';
import { useAuthWrapper } from '@weareopenr/authkit';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { EProductLibraryStatus } from 'src/api/getProductLibrarySyncStatus/getProductLibrarySyncStatus';
import { EVENT_STATUSES, EVENT_TYPES } from 'src/api/getEvents';
import { publishProducts } from 'src/api/publishProducts/publishProducts';
import { PublishProductsType } from 'src/api/publishProducts/types';
import { CreateGlobalItemWizard } from 'src/components/CreateGlobalItemWizard/CreateGlobalItemWizard';
import { CancelScheduledProductPublicationPrompt } from 'src/components/CancelScheduledProductPublicationPrompt/CancelScheduledProductPublicationPrompt';
import { OptionGroupsTab } from 'src/components/OptionGroupsTab';
import { PageHeadingWithSync, IHeadingOptionMenuItem } from 'src/components/PageHeadingWithSync/PageHeadingWithSync';
import { ProductPublishModal } from 'src/components/ProductPublishModal/ProductPublishModal';
import { ProductsTab } from 'src/components/ProductsTab';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { SyncReportModal } from 'src/components/SyncReportModal/SyncReportModal';
import { getSyncEndpoint } from 'src/helpers/getSyncEndpoint/getSyncEndpoint';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useEvents } from 'src/hooks/useEvents';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';
import {
  IProductLibraryStatus,
  useProductLibraryStatus,
} from 'src/hooks/useProductLibraryStatus/useProductLibraryStatus';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useCommitIngest } from 'src/hooks/useCommitIngest/useCommitIngest';
import { useIsIngestSync } from 'src/hooks/useIsIngestSync/useIsIngestSync';
import { useIngestDiff } from 'src/hooks/useIngestDiff/useIngestDiff';
import { useRejectSync } from 'src/hooks/useRejectSync/useRejectSync';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { CreateOptionGroupModal } from 'src/components/CreateOptionGroupModal/CreateOptionGroupModal';
import { FEATURE_FLAGS, IntegrationType } from 'src/constants';
import { Routes } from 'src/routes';
import styles from './Items.module.scss';

export interface RangeOption {
  id: string;
  label: string;
  display_order: number;
}

export const Items = (): ReactElement => {
  const [isItemWizardOpen, setIsItemWizardOpen] = useState<boolean>(false);
  const [isOptionGroupModalOpen, setIsOptionGroupModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProductPublishOpen, setIsProductPublishOpen] = useState<boolean>(false);
  const [isCancelPublishPromptOpen, setIsCancelPublishPromptOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { trackEvent } = useUserTracking();
  const { refetch: refetchExternalSystems, data: externalSystems = [] } = useExternalSystems();
  const { approveSync: approveIngestSync, rejectSync: rejectIngestSync } = useCommitIngest();
  const { rejectSync: rejectLegacySync } = useRejectSync();
  const { featureFlags } = useAuthWrapper();
  const withIndependentOptionGroups = featureFlags.includes(FEATURE_FLAGS.INDEPENDENT_OPTION_GROUPS);

  const onSyncStatusUpdate = (data: IProductLibraryStatus) => {
    // only refetch products after status changes from PENDING -> SUCCESS sync
    // status call count avoids refetch on page render if a previous sync was successful
    if (data?.sync_status === EProductLibraryStatus.SUCCESS && data.callCount > 1) {
      queryClient.refetchQueries({ queryKey: [QueryKeys.productSizes] });
      refetchExternalSystems();
    }
  };
  const isProductSyncEnabled = useMemo(() => !!getSyncEndpoint(externalSystems), [externalSystems]);
  const { isSyncInProgress, triggerSync, syncInfo, eventId } = useProductLibraryStatus({
    onSyncStatusUpdate,
    enabled: isProductSyncEnabled,
  });

  // A notification deep-links to a specific event; otherwise review whatever sync is current.
  const urlEventId = useMemo(() => params.get('event_id') ?? undefined, [params]);
  const reviewEventId = urlEventId ?? eventId;

  // service-ingest is opt-in per integration, so both pipelines are live and the sync itself decides
  // which commit endpoint the review modal uses.
  const { isIngestSync, isResolvingSyncPipeline, isPendingReview } = useIsIngestSync(reviewEventId);

  // The item-level diff in the status response covers items only. This is fetched purely to state
  // the option group and modifier counts before the user accepts.
  const { data: ingestDiff } = useIngestDiff(reviewEventId, { enabled: isIngestSync });

  // PageHeadingWithSync's reject prop takes no arguments, so the page supplies the event id.
  // The legacy endpoint has no event to supply.
  const handleRejectSync = () => {
    // Rejecting down the wrong pipeline fails, so wait for the discriminator rather than assume
    // legacy from an unresolved `isIngestSync`.
    if (isResolvingSyncPipeline) return;
    if (!isIngestSync) return rejectLegacySync();
    if (reviewEventId) void rejectIngestSync(reviewEventId);
  };

  // Only set for ingest syncs: legacy accept stays inside the modal, which posts product overrides
  // that /ingest/commit has no equivalent for.
  const handleAcceptIngestSync = isIngestSync && reviewEventId ? () => approveIngestSync(reviewEventId) : undefined;

  const isPosSystemEnabled = externalSystems.some(
    ({ integrationType }) => integrationType === IntegrationType.POINT_OF_SALE_MANAGEMENT,
  );
  const { data: events } = useEvents({ event_type: EVENT_TYPES.PUBLISH }, { enabled: isPosSystemEnabled });

  const { isPublishScheduled, scheduledPublishEvent, latestPublishEvent } = useMemo(() => {
    if (!events?.data?.length) {
      return { isPublishScheduled: false, scheduledPublishEvent: null, latestPublishEvent: null };
    }

    const scheduledEvent = events.data.find((event) => event.status === EVENT_STATUSES.SCHEDULED) || null;
    const latestEvent = scheduledEvent || events.data[0] || null;

    return {
      isPublishScheduled: !featureFlags.includes(FEATURE_FLAGS.ENHANCED_SCHEDULING) && !!scheduledEvent,
      scheduledPublishEvent: scheduledEvent,
      latestPublishEvent: latestEvent,
    };
  }, [events, featureFlags]);

  const handlePublishClick = async () => {
    if (featureFlags.includes(FEATURE_FLAGS.PRODUCT_PUBLISH_FLOW)) {
      setIsProductPublishOpen(true);
    } else {
      try {
        setIsLoading(true);
        await publishProducts({ publish_type: [PublishProductsType.PRODUCT] });
      } catch (error) {
        console.error('Error publishing products:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancelScheduledPublish = () => {
    setIsCancelPublishPromptOpen((prev) => !prev);
  };

  const { data: ranges, isLoading: isRangesLoading } = useRanges();
  const rangeOptions: RangeOption[] = useMemo(
    () => ranges?.map((range, index) => ({ id: range.range_id, label: range.name, display_order: index })) || [],
    [ranges],
  );

  useEffect(() => {
    document.title = 'Items';
    trackEvent('page_viewed', { page: 'product_library' });
  }, [trackEvent]);

  const optionMenuItems: IHeadingOptionMenuItem[] = [
    {
      name: 'New product',
      onOptionClick: () => setIsItemWizardOpen(true),
      id: 'add-product',
      isDisabled: isSyncInProgress,
      icon: Plus,
    },
    ...(withIndependentOptionGroups
      ? [
          {
            name: 'New option group',
            onOptionClick: () => setIsOptionGroupModalOpen(true),
            id: 'add-option-group',
            isDisabled: isSyncInProgress,
            icon: Plus,
          },
        ]
      : []),
    ...(isPosSystemEnabled
      ? [
          {
            name: 'View Publish Status',
            onOptionClick: () =>
              latestPublishEvent && navigate(`${location.pathname}/status/${latestPublishEvent.event_id}`),
            id: 'view-publish-status',
            isDisabled: isSyncInProgress || !latestPublishEvent,
            icon: ViewIcon,
          },
        ]
      : []),
  ];

  const publishButton = isPosSystemEnabled ? (
    isPublishScheduled ? (
      <PrimaryButton onClick={handleCancelScheduledPublish} icon={TimerIcon}>
        Scheduled
      </PrimaryButton>
    ) : (
      <PrimaryButton onClick={handlePublishClick} disabled={isLoading || isSyncInProgress} icon={Export}>
        Publish
      </PrimaryButton>
    )
  ) : undefined;

  return (
    <TabsProvider defaultTab="products">
      <div className={styles['page-container']}>
        <PageHeadingWithSync
          optionMenuItems={optionMenuItems}
          entityLabel="Products"
          isSyncEnabled={true}
          isSyncInProgress={isSyncInProgress}
          triggerSync={triggerSync}
          supportsInstanceSelection={true}
          rejectSync={handleRejectSync}
          syncStatus={syncInfo?.sync_status}
          // /items/sync/status reports one connected instance at a time, so a review pending on
          // another instance is invisible there. The notification is event-scoped and knows.
          hasPendingReview={isIngestSync && isPendingReview}
          reviewRoutePath={Routes.PRODUCT_LIBRARY}
          renderReviewModal={({ isOpen, closeModal }) => (
            // Held shut until the pipeline is known: both flows render the same item diff, but they
            // commit through different endpoints and accepting down the wrong one fails.
            <SyncReportModal
              isOpen={isOpen && !isResolvingSyncPipeline}
              closeModal={closeModal}
              products={syncInfo?.diff}
              rejectSync={handleRejectSync}
              onAccept={handleAcceptIngestSync}
              ingestSummary={ingestDiff?.summary}
            />
          )}
          primaryAction={publishButton}
          extraModals={
            <>
              <ProductPublishModal isOpen={isProductPublishOpen} closeModal={() => setIsProductPublishOpen(false)} />
              <CancelScheduledProductPublicationPrompt
                event={scheduledPublishEvent}
                isOpen={isCancelPublishPromptOpen}
                closePrompt={handleCancelScheduledPublish}
              />
            </>
          }
        />
        <CreateGlobalItemWizard open={isItemWizardOpen} closeModal={() => setIsItemWizardOpen(false)} />
        <CreateOptionGroupModal open={isOptionGroupModalOpen} onClose={() => setIsOptionGroupModalOpen(false)} />

        {isRangesLoading ? (
          <SpinnerContainer>
            <Spinner size="xlarge" colour="rainbow" />
          </SpinnerContainer>
        ) : (
          <>
            {withIndependentOptionGroups && (
              <TabList>
                <Tab id="products" tabText="Products" />
                <Tab id="option-groups" tabText="Option Groups" />
              </TabList>
            )}
            <TabContent id="products">
              <ProductsTab
                isSyncInProgress={isSyncInProgress}
                onOpenItemWizard={() => setIsItemWizardOpen(true)}
                rangeOptions={rangeOptions}
              />
            </TabContent>
            {withIndependentOptionGroups && (
              <TabContent id="option-groups">
                <OptionGroupsTab
                  isSyncInProgress={isSyncInProgress}
                  onOpenOptionGroupModal={() => setIsOptionGroupModalOpen(true)}
                  rangeOptions={rangeOptions}
                />
              </TabContent>
            )}
          </>
        )}
      </div>
    </TabsProvider>
  );
};
